import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";
import { INFLUENCER_CAMPAIGN_QUERIES } from "@/marketing/infrastructure/influencer/campaign/utils/queries";
import { CampaignOperationsKind, type CampaignOperationsFields } from "@/marketing/models/campaignOperations";
import { InfluencerCampaignStatus, type InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { CampaignOperationsConflicts } from "./campaignOperationsConflicts";
import { CampaignOperationsPatches, type CampaignTrackerCellValue } from "./campaignOperationsPatches";
import { CampaignTrackerRows } from "./campaignTrackerRows";
import { TrackerColumns, type TrackerEditableColumn } from "./trackerColumns";
import { TrackerHistoryDialog } from "./TrackerHistoryDialog";
import { CampaignTrackerGrid } from "./CampaignTrackerGrid";
import { CampaignTrackerSaves } from "./campaignTrackerSaves";
import { TrackerCellEdits, type TrackerCellEditStart, type TrackerSaveOutcome } from "./trackerCellEdits";

interface CampaignTrackerViewProps {
  // Calendar day used for deadlines; injectable so tests do not depend on the clock.
  today?: string;
  onOpenCampaign: (campaign: InfluencerCampaign) => void;
}

type StatusFilter = InfluencerCampaignStatus | "all";
type QuickFilter = "all" | "due" | "atRisk" | "changed";

const QUICK_FILTER_LABELS: Record<QuickFilter, string> = {
  all: "All",
  due: "Due in 7 days or late",
  atRisk: "Posting at risk",
  changed: "Changed since yesterday",
};

const DUE_SOON_DAYS = 7;
const DAY_MS = 86_400_000;

function localCalendarDay(): string {
  return new Date().toLocaleDateString("en-CA");
}

/** Replaces the INFLUENCER MASTER TRACKER sheet: every campaign in one grid, edited in place. */
export function CampaignTrackerView({ today = localCalendarDay(), onOpenCampaign }: CampaignTrackerViewProps) {
  const queryClient = useQueryClient();
  const editorEmail = useAuthStore((state) => state.user?.email);
  const lookupBrand = useBrandLookup();
  // Shared per query client so pending saves survive leaving and reopening the view.
  const saves = CampaignTrackerSaves.for(queryClient);
  // No automatic refetch (focus, reopen, reconnect) while a save is pending: it would show older server data over the edit.
  const refetchWhenIdle = () => !saves.hasPending();
  const { data: campaigns = [], isLoading, isError } = useQuery({
    ...INFLUENCER_CAMPAIGN_QUERIES.tracker(),
    refetchOnWindowFocus: refetchWhenIdle,
    refetchOnMount: refetchWhenIdle,
    refetchOnReconnect: refetchWhenIdle,
  });

  const [kind, setKind] = useState<CampaignOperationsKind>(CampaignOperationsKind.Standard);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(InfluencerCampaignStatus.InProgress);
  const [picFilter, setPicFilter] = useState("");
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [historyCampaign, setHistoryCampaign] = useState<InfluencerCampaign | null>(null);

  const columns = kind === CampaignOperationsKind.Paid ? TrackerColumns.PAID : TrackerColumns.STANDARD;
  const editableColumns = columns.filter(TrackerColumns.isEditable);
  const bulkColumn: TrackerEditableColumn | undefined = editableColumns.find((column) => column.field === bulkField);

  const picOptions = useMemo(() => {
    const names = campaigns.flatMap((campaign) => [campaign.operations?.pics?.brand, campaign.operations?.pics?.scouting, campaign.operations?.pics?.content]);
    return [...new Set(names.filter((name): name is string => !!name))].sort();
  }, [campaigns]);

  const countsByKind = useMemo(() => {
    const counts: Record<CampaignOperationsKind, number> = { [CampaignOperationsKind.Standard]: 0, [CampaignOperationsKind.Paid]: 0 };
    for (const campaign of campaigns) counts[campaign.operations?.kind ?? CampaignOperationsKind.Standard] += 1;
    return counts;
  }, [campaigns]);

  const rows = useMemo(() => {
    const changedSince = new Date(Date.parse(`${today}T00:00:00`) - DAY_MS).toISOString();
    const matching = campaigns.filter((campaign) => {
      const filter = { kind, ...(statusFilter !== "all" ? { status: statusFilter } : {}), pic: picFilter, search };
      if (!CampaignTrackerRows.matches(campaign, filter)) return false;
      if (quickFilter === "due") {
        const deadline = CampaignTrackerRows.nextDeadline(campaign, today);
        return !!deadline && deadline.daysLeft <= DUE_SOON_DAYS;
      }
      if (quickFilter === "atRisk") return ["At Risk", "Off Track"].includes(campaign.operations?.phases?.posting?.status ?? "");
      if (quickFilter === "changed") return CampaignTrackerRows.changedFieldsSince(campaign, changedSince).length > 0;
      return true;
    });
    return matching.sort((left, right) => {
      const leftDue = CampaignTrackerRows.nextDeadline(left, today)?.dueDate ?? "9999-12-31";
      const rightDue = CampaignTrackerRows.nextDeadline(right, today)?.dueDate ?? "9999-12-31";
      return leftDue.localeCompare(rightDue) || left.name.localeCompare(right.name);
    });
  }, [campaigns, kind, statusFilter, picFilter, search, quickFilter, today]);

  // editStart is omitted by bulk edits and history restores, which start from what the cell shows now.
  const saveField = (campaign: InfluencerCampaign, field: string, value: CampaignTrackerCellValue, editStart?: TrackerCellEditStart): Promise<TrackerSaveOutcome> =>
    saves.track(async () => {
      const patch = CampaignOperationsPatches.forField(field, value);
      const trackerKey = INFLUENCER_CAMPAIGN_KEYS.tracker();
      const cellKey = TrackerCellEdits.key(campaign.campaignId, field);
      // An editor opened before a conflict on this cell was editing a value the server no longer holds.
      const generation = editStart?.generation ?? saves.cellEdits.generation(cellKey);
      if (generation !== saves.cellEdits.generation(cellKey)) return "stale";
      const applyToCampaign = (cellPatch: CampaignOperationsFields) =>
        queryClient.setQueryData<InfluencerCampaign[]>(trackerKey, (current) =>
          current?.map((stored) => (stored.campaignId === campaign.campaignId ? { ...stored, operations: CampaignOperationsPatches.applyLocally(stored.operations, cellPatch) } : stored)),
        );
      // A refetch already in flight would overwrite the optimistic value with older server data.
      await queryClient.cancelQueries({ queryKey: trackerKey });
      const cachedCampaign = queryClient.getQueryData<InfluencerCampaign[]>(trackerKey)?.find((stored) => stored.campaignId === campaign.campaignId);
      const shownValue = CampaignOperationsPatches.read((cachedCampaign ?? campaign).operations, field);
      const baseValue = editStart?.value === undefined ? shownValue : editStart.value;
      const editNumber = saves.cellEdits.begin(cellKey, baseValue, CampaignOperationsPatches.read(patch, field));
      // Show the edit immediately; the refetch after saving brings back server history.
      applyToCampaign(patch);
      let outcome = "superseded" as TrackerSaveOutcome;
      let conflictHandled = false;
      try {
        await saves.enqueue(campaign.campaignId, async () => {
          // A conflict on this cell after the edit started means it was made without seeing the other editor's value.
          if (saves.cellEdits.generation(cellKey) !== generation) return;
          try {
            await influencerCampaignAPIClient.updateInfluencerCampaign(campaign.campaignId, {
              operations: patch,
              // Read when the queued save runs: earlier saves to this cell have settled, so this is what the server holds.
              operationsExpected: { [field]: saves.cellEdits.confirmed(cellKey) ?? null },
              ...(editorEmail ? { operationsEditor: editorEmail } : {}),
            });
            outcome = "saved";
          } catch (error) {
            // Record the conflict before the queue runs the next save, so a later edit of this cell sees the new generation.
            const conflict = CampaignOperationsConflicts.from(error).find((candidate) => candidate.field === field);
            if (conflict) {
              conflictHandled = true;
              applyToCampaign(CampaignOperationsPatches.forStoredValue(field, saves.cellEdits.reject(cellKey, editNumber, conflict.current)));
            }
            throw error;
          }
        });
        const shownAfterSave = outcome === "saved" ? saves.cellEdits.succeed(cellKey, editNumber) : saves.cellEdits.fail(cellKey, editNumber);
        applyToCampaign(CampaignOperationsPatches.forStoredValue(field, shownAfterSave));
        return outcome;
      } catch (error) {
        if (!conflictHandled) applyToCampaign(CampaignOperationsPatches.forStoredValue(field, saves.cellEdits.fail(cellKey, editNumber)));
        throw error;
      }
    });

  const refresh = async () => {
    // A refresh while saves are pending would show older server data over a newer pending edit.
    if (saves.hasPending()) return;
    await queryClient.invalidateQueries({ queryKey: INFLUENCER_CAMPAIGN_KEYS.lists() });
  };

  const commitCell = (campaign: InfluencerCampaign, field: string, value: CampaignTrackerCellValue, editStart?: TrackerCellEditStart) => {
    const fieldLabel = editableColumns.find((column) => column.field === field)?.header ?? field;
    saveField(campaign, field, value, editStart)
      .then((outcome) => {
        // A superseded edit was dropped after its cell's conflict, which already has a toast.
        if (outcome === "stale") toast.error(`${campaign.name}: ${fieldLabel} changed while you were editing, so your edit was not saved.`);
      })
      .catch((error: unknown) => {
        const conflict = CampaignOperationsConflicts.from(error).find((candidate) => candidate.field === field);
        if (conflict) {
          toast.error(CampaignOperationsConflicts.describe(campaign.name, fieldLabel, conflict));
          return;
        }
        toast.error(`Could not save ${campaign.name}: ${error instanceof Error ? error.message : "unknown error"}`);
      })
      .finally(() => void refresh());
  };

  // Selection survives filter changes, but only visible rows are updated and counted.
  const selectedRows = rows.filter((campaign) => selectedIds.has(campaign.campaignId));

  const applyBulk = async () => {
    if (!bulkColumn) return;
    const targets = selectedRows;
    const value: CampaignTrackerCellValue = bulkColumn.editor === "boolean" ? (bulkValue === "" ? null : bulkValue === "Yes") : bulkValue;
    const failed: string[] = [];
    for (const campaign of targets) {
      try {
        if ((await saveField(campaign, bulkColumn.field, value)) !== "saved") failed.push(campaign.name);
      } catch {
        failed.push(campaign.name);
      }
    }
    await refresh();
    if (failed.length > 0) toast.error(`Updated ${targets.length - failed.length} of ${targets.length}; failed: ${failed.join(", ")}`);
    else toast.success(`Updated ${targets.length} campaigns`);
    setSelectedIds(new Set());
  };

  const toggle = (campaignId: string) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((current) => (rows.every((campaign) => current.has(campaign.campaignId)) ? new Set() : new Set(rows.map((campaign) => campaign.campaignId))));

  const switchKind = (nextKind: CampaignOperationsKind) => {
    setKind(nextKind);
    setSelectedIds(new Set());
    setBulkField("");
  };

  const selectClass = "tw-h-8 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2 tw-text-xs";

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-2">
      <div role="tablist" className="tw-flex tw-gap-1 tw-border-b tw-border-border">
        {[CampaignOperationsKind.Standard, CampaignOperationsKind.Paid].map((tabKind) => (
          <button
            key={tabKind}
            type="button"
            role="tab"
            aria-selected={kind === tabKind}
            onClick={() => switchKind(tabKind)}
            className={cn("tw-border-b-2 tw-px-3 tw-py-1.5 tw-text-sm", kind === tabKind ? "tw-border-primary tw-font-semibold" : "tw-border-transparent tw-text-muted-foreground")}
          >
            {tabKind === CampaignOperationsKind.Paid ? "Paid Campaign Tracker" : "Campaign Tracker"} ({countsByKind[tabKind]})
          </button>
        ))}
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <select aria-label="Campaign status" className={selectClass} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
          <option value="all">All statuses</option>
          {Object.values(InfluencerCampaignStatus).map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        <select aria-label="PIC" className={selectClass} value={picFilter} onChange={(event) => setPicFilter(event.target.value)}>
          <option value="">All PICs</option>
          {picOptions.map((pic) => (
            <option key={pic} value={pic}>
              {pic}
            </option>
          ))}
        </select>
        <Input aria-label="Search campaigns" placeholder="Search campaign or PIC" value={search} onChange={(event) => setSearch(event.target.value)} className="tw-h-8 tw-w-56 tw-text-xs" />
        <div className="tw-flex tw-gap-1">
          {(Object.keys(QUICK_FILTER_LABELS) as QuickFilter[]).map((filter) => (
            <Button key={filter} size="sm" variant={quickFilter === filter ? "default" : "outline"} className="tw-h-8 tw-text-xs" onClick={() => setQuickFilter(filter)}>
              {QUICK_FILTER_LABELS[filter]}
            </Button>
          ))}
        </div>
        <span className="tw-ml-auto tw-text-xs tw-text-muted-foreground">{rows.length} campaigns</span>
      </div>

      {selectedIds.size > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-primary/30 tw-bg-primary/5 tw-px-3 tw-py-2 tw-text-xs">
          <span className="tw-font-medium">{selectedIds.size} selected</span>
          <select aria-label="Bulk field" className={selectClass} value={bulkField} onChange={(event) => {
            setBulkField(event.target.value);
            setBulkValue("");
          }}>
            <option value="">Choose a column…</option>
            {editableColumns.map((column) => (
              <option key={column.field} value={column.field}>
                {column.header}
              </option>
            ))}
          </select>
          {bulkColumn && (bulkColumn.editor === "status" || bulkColumn.editor === "boolean") ? (
            <select aria-label="Bulk value" className={selectClass} value={bulkValue} onChange={(event) => setBulkValue(event.target.value)}>
              <option value="">— (clear)</option>
              {(bulkColumn.editor === "boolean" ? ["Yes", "No"] : (bulkColumn.options ?? [])).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <Input
              aria-label="Bulk value"
              type={bulkColumn?.editor === "date" ? "date" : "text"}
              list={bulkColumn?.editor === "pic" ? "tracker-bulk-pics" : undefined}
              placeholder="Value (empty clears)"
              value={bulkValue}
              onChange={(event) => setBulkValue(event.target.value)}
              className="tw-h-8 tw-w-56 tw-text-xs"
            />
          )}
          <datalist id="tracker-bulk-pics">
            {picOptions.map((pic) => (
              <option key={pic} value={pic} />
            ))}
          </datalist>
          <Button size="sm" className="tw-h-8 tw-text-xs" disabled={!bulkColumn || selectedRows.length === 0} onClick={() => void applyBulk()}>
            Apply to {selectedRows.length}
          </Button>
          <Button size="sm" variant="ghost" className="tw-h-8 tw-text-xs" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {isError && campaigns.length > 0 && (
        <div role="status" className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-3 tw-py-1.5 tw-text-xs tw-text-amber-800">
          Could not refresh campaigns. Showing the last loaded data, which may be out of date.
        </div>
      )}

      <div className="tw-min-h-0 tw-flex-1">
        {isLoading ? (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">Loading campaigns…</div>
        ) : isError && campaigns.length === 0 ? (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-destructive">Could not load campaigns. Refresh to try again.</div>
        ) : (
          <CampaignTrackerGrid
            campaigns={rows}
            columns={columns}
            today={today}
            selectedIds={selectedIds}
            picOptions={picOptions}
            brandName={(brandId) => lookupBrand(brandId)?.name}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onCommit={commitCell}
            saveStateOf={(campaignId, field) => saves.cellEdits.saveState(TrackerCellEdits.key(campaignId, field))}
            onOpenCampaign={onOpenCampaign}
            onShowHistory={setHistoryCampaign}
          />
        )}
      </div>

      <TrackerHistoryDialog
        campaign={historyCampaign}
        onClose={() => setHistoryCampaign(null)}
        onRestore={(campaign, field, value) => {
          commitCell(campaign, field, value);
          setHistoryCampaign(null);
        }}
      />
    </div>
  );
}
